// src/pages/PrivacyPolicyPage.jsx
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const lastUpdated = "May 10, 2025";
  const email = "support@smartaicareer.com";
  const appName = "ResumeScoreAI";
  const domain = "smartaicareer.com";

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* NAV */}
      <nav style={{ borderBottom: "1px solid #e2e8f0", padding: "0 5%", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", position: "sticky", top: 0, zIndex: 100 }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <div style={{ width: 32, height: 32, background: "linear-gradient(135deg, #2563eb, #1d4ed8)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>R</span>
          </div>
          <span style={{ fontWeight: 800, fontSize: 18, color: "#0f172a", letterSpacing: "-0.03em" }}>
            ResumeScore<span style={{ color: "#2563eb" }}>AI</span>
          </span>
        </Link>
        <button
          onClick={() => navigate(user ? "/dashboard" : "/")}
          style={{ padding: "8px 18px", background: "transparent", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, fontWeight: 500, fontSize: 14, cursor: "pointer" }}
        >
          ← Back
        </button>
      </nav>

      {/* CONTENT */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 5% 80px" }}>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: "inline-block", background: "#eff6ff", color: "#2563eb", fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 20, marginBottom: 16, border: "1px solid #bfdbfe" }}>
            Legal
          </div>
          <h1 style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800, color: "#0f172a", marginBottom: 8, letterSpacing: "-0.02em" }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: 14, color: "#64748b" }}>Last updated: {lastUpdated}</p>
        </div>

        {/* Intro */}
        <div style={{ background: "#eff6ff", borderLeft: "4px solid #2563eb", padding: "16px 20px", borderRadius: 8, marginBottom: 36, fontSize: 14, color: "#1e40af", lineHeight: 1.7 }}>
          This Privacy Policy explains how {appName} ("we", "our", or "us") collects, uses, and protects your personal information when you use our website at {domain} and related services.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>

          <Section title="1. Information We Collect">
            <p>We collect the following information when you use {appName}:</p>
            <ul>
              <li><strong>Account information:</strong> Your name and email address when you sign up.</li>
              <li><strong>Resume content:</strong> Text from resumes you upload or paste for analysis. This is processed to provide ATS scoring and feedback.</li>
              <li><strong>Job descriptions:</strong> Text you paste for comparison against your resume.</li>
              <li><strong>Payment information:</strong> Payments are processed by Stripe. We do not store your card details — Stripe handles all payment data securely.</li>
              <li><strong>Usage data:</strong> Analysis history, transaction records, and activity logs stored in Firebase to provide our service.</li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide and improve our ATS resume analysis service</li>
              <li>Process payments securely via Stripe</li>
              <li>Send transactional emails (payment confirmations, analysis results)</li>
              <li>Respond to support requests</li>
              <li>Maintain your analysis history in your dashboard</li>
              <li>Detect and prevent fraudulent activity</li>
            </ul>
            <p>We do <strong>not</strong> sell your personal data to third parties. We do not use your resume content for advertising purposes.</p>
          </Section>

          <Section title="3. Data Storage & Security">
            <p>Your data is stored securely using industry-standard cloud infrastructure:</p>
            <ul>
              <li><strong>Google cloud services:</strong> User accounts, analysis history, and transaction records are stored securely using Google's cloud infrastructure</li>
              <li><strong>Stripe:</strong> Payment processing with bank-level encryption — we never store your card details</li>
              <li><strong>Secure cloud hosting:</strong> Our application runs on encrypted, professionally managed cloud servers</li>
            </ul>
            <p>We implement industry-standard security measures including HTTPS encryption, secure authentication, and access controls. However, no method of transmission over the internet is 100% secure.</p>
          </Section>

          <Section title="4. Resume Data">
            <p>Your resume text is processed by our AI system (Google Gemini) solely to generate your ATS analysis. We do not:</p>
            <ul>
              <li>Share your resume content with third parties for marketing purposes</li>
              <li>Use your resume to train AI models</li>
              <li>Sell your resume data</li>
            </ul>
            <p>Analysis results are stored in your account so you can access your history. You can request deletion of your data at any time by contacting us.</p>
          </Section>

          <Section title="5. Cookies">
            <p>We use minimal cookies necessary for the service to function, including:</p>
            <ul>
              <li><strong>Authentication cookies:</strong> To keep you logged in (managed by Firebase)</li>
              <li><strong>Session cookies:</strong> To maintain your session state</li>
            </ul>
            <p>We do not use advertising or tracking cookies.</p>
          </Section>

          <Section title="6. Third-Party Services">
          <p>We work with carefully selected third-party providers. The ones that handle your personal data are:</p>
            <ul>
              <li><strong>Google:</strong> Cloud infrastructure for authentication and data storage — <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>privacy policy</a></li>
              <li><strong>Stripe:</strong> Secure payment processing — <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>privacy policy</a></li>
            </ul>
            <p>We also use trusted providers for AI-powered resume analysis, transactional email delivery, and cloud hosting infrastructure. These providers process data only as necessary to deliver our service and are bound by confidentiality obligations.</p>
          </Section>

          <Section title="7. Data Retention">
            <p>We retain your data for as long as your account is active. If you delete your account or request data deletion:</p>
            <ul>
              <li>Your personal information will be removed within 30 days</li>
              <li>Payment records may be retained for up to 7 years as required by financial regulations</li>
              <li>Anonymized usage statistics may be retained indefinitely</li>
            </ul>
          </Section>

          <Section title="8. Your Rights">
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access</strong> the personal data we hold about you</li>
              <li><strong>Correct</strong> inaccurate personal data</li>
              <li><strong>Delete</strong> your account and associated data</li>
              <li><strong>Export</strong> your data in a portable format</li>
              <li><strong>Opt out</strong> of non-essential communications</li>
            </ul>
            <p>To exercise any of these rights, contact us at <a href={`mailto:${email}`} style={{ color: "#2563eb" }}>{email}</a>.</p>
          </Section>

          <Section title="9. Children's Privacy">
            <p>{appName} is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us immediately.</p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page with an updated date. Your continued use of the service after changes constitutes acceptance of the new policy.</p>
          </Section>

          <Section title="11. Contact Us">
            <p>If you have any questions about this Privacy Policy or how we handle your data, please contact us:</p>
            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "16px 20px", marginTop: 12, border: "1px solid #e2e8f0" }}>
              <p style={{ margin: "4px 0", fontSize: 14, color: "#374151" }}><strong>{appName}</strong></p>
              <p style={{ margin: "4px 0", fontSize: 14, color: "#374151" }}>📧 <a href={`mailto:${email}`} style={{ color: "#2563eb" }}>{email}</a></p>
              <p style={{ margin: "4px 0", fontSize: 14, color: "#374151" }}>🌐 <a href={`https://${domain}`} style={{ color: "#2563eb" }}>{domain}</a></p>
            </div>
          </Section>

        </div>

        {/* Footer nav */}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: "1px solid #e2e8f0", display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Link to="/terms" style={{ fontSize: 14, color: "#2563eb", textDecoration: "none", fontWeight: 500 }}>Terms of Service →</Link>
          <Link to="/support" style={{ fontSize: 14, color: "#2563eb", textDecoration: "none", fontWeight: 500 }}>Contact Support →</Link>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ borderTop: "1px solid #e2e8f0", padding: "16px 5%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>ResumeScore<span style={{ color: "#2563eb" }}>AI</span></span>
        <span style={{ fontSize: 13, color: "#94a3b8" }}>© {new Date().getFullYear()} ResumeScoreAI. All rights reserved.</span>
      </footer>
    </div>
  );
}

// ── Reusable section component ──
function Section({ title, children }) {
  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #f1f5f9", letterSpacing: "-0.01em" }}>
        {title}
      </h2>
      <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.8 }}>
        {children}
      </div>
      <style>{`
        div p { margin: 0 0 12px 0; }
        div ul { margin: 8px 0 12px 0; padding-left: 20px; }
        div ul li { margin-bottom: 6px; }
        div a { color: #2563eb; }
        div a:hover { text-decoration: underline; }
      `}</style>
    </div>
  );
}
